import { getRouteApi } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import LinkText from '@/components/LinkText/LinkText'
import Table, { type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import Tag from '@/components/Tag/Tag'
import ToggleGroup, {
  type ToggleGroupOption
} from '@/components/ToggleGroup/ToggleGroup'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import { useColumns } from '@/hooks/useColumns'
import { useDebounceSearch } from '@/hooks/useDebounceSearch'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { useSorting } from '@/hooks/useSorting'
import useAuthStore from '@/store/useAuthStore'
import type { Semester } from '@/types/semester'
import { formatDate } from '@/utils/format-date'

import styles from './SchoolYearsView.module.scss'
import { schoolYearStatusToTagMap } from '../constants/school-year-status-to-tag-map'
import useSchoolYearsDownload from '../mutations/useSchoolYearsDownload'
import { useSchoolYears } from '../queries/useSchoolYears'
import useSchoolYearsCount from '../queries/useSchoolYearsCount'
import type {
  SchoolYear,
  SchoolYearStatus,
  SchoolYearAndBreakToggle
} from '../types'

const routeApi = getRouteApi('/_auth/school-settings/years/')

const SchoolYearsView = () => {
  const { t } = useTranslation('schoolSettings')

  const { user } = useAuthStore()

  const { isMobile } = useScreenResolution()

  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const pagination = usePagination(routeApi)
  const sorting = useSorting(routeApi)

  const { data: schoolYears, isLoading: isSchoolYearsLoading } =
    useSchoolYears(search)

  const { data: schoolYearsCount, isLoading: isSchoolYearsCountLoading } =
    useSchoolYearsCount({
      status: search.status
    })

  const renderSemesterDates = (value: Semester) =>
    `${formatDate(value.startDate)} – ${formatDate(value.endDate)}`

  const { columns, ...manageColumns } = useColumns<SchoolYear>(
    [
      {
        key: 'name',
        label: t('header.school-year'),
        render: (value: SchoolYear) => (
          <LinkText
            to="/school-settings/years/$yearId"
            params={{
              yearId: value.id
            }}
          >
            {value.name}
          </LinkText>
        ),
        sorterKey: 'start_date',
        width: isMobile ? TABLE_CELL_WIDTH.MD : undefined
      },
      {
        label: t('header.semester-1'),
        dataIndex: ['firstSemester'],
        key: 'first_semester',
        render: (value: Semester) => renderSemesterDates(value),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined
      },
      {
        label: t('header.semester-2'),
        dataIndex: ['secondSemester'],
        key: 'second_semester',
        render: (value: Semester) => renderSemesterDates(value),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined
      },
      {
        dataIndex: 'status',
        key: 'status',
        label: t('header.status'),
        width: 120,
        render: (value: SchoolYearStatus) => (
          <Tag {...schoolYearStatusToTagMap(t)[value]} />
        )
      }
    ],
    { routeApi }
  )

  const handleToggle = (value: SchoolYearAndBreakToggle) => {
    navigate({
      search: prev => ({
        ...prev,
        status: value,
        page: 1
      })
    })
  }

  const { searchValue, handleChangeSearch } = useDebounceSearch(routeApi)

  const toggleOptions: ToggleGroupOption<SchoolYearAndBreakToggle>[] = [
    {
      label: t('button.active-and-future'),
      value: 'active-and-future'
    },
    {
      label: t('button.past'),
      value: 'past'
    }
  ]

  const filters: Filter[] = [
    {
      label: t('label.search'),
      variant: 'input',
      size: 'wide',
      filterProps: {
        id: 'search',
        value: searchValue,
        type: 'search',
        dataTestId: 'search',
        placeholder: t('label.search'),
        onChange: value => handleChangeSearch(value)
      }
    }
  ]

  const actions: (row: SchoolYear) => TableAction<SchoolYear>[] = row =>
    user?.isSuperAdmin
      ? [
          {
            icon: <EditIcon />,
            text: t('button.edit'),
            hidden: row.status !== 'future',
            onClick: (item: SchoolYear) => {
              navigate({
                to: '/school-settings/years/$yearId/edit',
                params: { yearId: item.id }
              })
            }
          }
        ].filter(item => !item.hidden)
      : []

  const download = useSchoolYearsDownload({
    ...search,
    columnsKey: manageColumns.activeColumnKeys
  })

  return (
    <>
      <Filters
        filters={filters}
        hideClearAllButton
        manageColumns={manageColumns}
        actionsRender={
          <>
            <ExportButton {...download} />
            {user?.isSuperAdmin ? (
              <Button asLink to="/school-settings/years/add" icon={<AddIcon />}>
                {t('button.add-new-school-year')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className={styles.toggleWrapper}>
        <ToggleGroup
          value={search.status}
          onChange={handleToggle}
          options={toggleOptions}
        />
      </div>

      <Table
        isLoading={isSchoolYearsLoading || isSchoolYearsCountLoading}
        id="school-years-table"
        data={schoolYears?.list || []}
        columns={columns}
        count={schoolYears?.count}
        totalCount={schoolYearsCount}
        actions={actions}
        {...pagination}
        {...sorting}
        caption={t('header.school-years')}
      />
    </>
  )
}
export default SchoolYearsView
